import { Injectable } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable, of, timeout, map, catchError, tap } from 'rxjs';
import { TypeLoading } from '@app/enums';
import { NEW_RELIC_EVENT } from '@app/enums/new-relic.enum';
import { Passenger } from '@app/models/micro/passenger.model';
import { Reservation } from '@app/models/micro/reservation.model';
import { AdminService } from '../admin.service';
import { LoadingService } from '../loading.service';
import { NewRelicEventsService } from '@app/services/new-relic-events.service';
import { CdcAttestationModalComponent } from '@shared/modals/cdc-attestation-modal/cdc-attestation-modal.component';
import {
  isCdcAttestationSaved,
  shouldShowCdcAttestation,
} from '@app/utils/functions/cdc-attestation';
import { UpdateDocuments } from './documents-update.service';

export type CdcAttestationResult = 'continue' | 'counter';

/** Tiempo maximo para asentar el SSR (updateSSR tiene p95 de 6.6 s en prod). */
const CDC_ATTESTATION_TIMEOUT_MS = 20000;

/**
 * CI-23290: Declaracion de salud CDC (RDC, 21 dias).
 * Primera pantalla despues de recuperar la reserva, antes de cualquier flujo.
 */
@Injectable({
  providedIn: 'root',
})
export class CdcAttestationService {
  constructor(
    private readonly _modalService: NgbModal,
    private readonly _adminService: AdminService,
    private readonly _loadingService: LoadingService,
    private readonly _updateDocuments: UpdateDocuments,
    private readonly _newRelicEventsService: NewRelicEventsService
  ) {}

  /**
   * Muestra el modal si aplica y resuelve cuando todos declararon ('continue')
   * o cuando la reserva debe ir a mostrador ('counter').
   */
  async run(reservation: Reservation): Promise<CdcAttestationResult> {
    const flagEnabled = this._adminService.admin?.showCdcAttestation ?? true;
    if (!shouldShowCdcAttestation(reservation, flagEnabled)) {
      return 'continue';
    }

    this._loadingService.hideLoading(TypeLoading.FULL_LOADING);
    this.track(NEW_RELIC_EVENT.CDC_ATTESTATION_SHOWN, reservation);

    const modalRef = this._modalService.open(CdcAttestationModalComponent, {
      centered: true,
      size: 'xl',
      backdrop: 'static',
      keyboard: false,
      windowClass: 'cdc-attestation-window',
    });
    modalRef.componentInstance.reservation = reservation;
    // Se pasan como funciones para no crear dependencia circular modal <-> servicio.
    modalRef.componentInstance.submitFn = (passenger: Passenger) =>
      this.submit(reservation, passenger);
    modalRef.componentInstance.trackFn = (
      event: NEW_RELIC_EVENT,
      passenger?: Passenger,
      extra?: Record<string, any>
    ) => this.track(event, reservation, passenger, extra);

    try {
      return (await modalRef.result) as CdcAttestationResult;
    } catch {
      return 'counter';
    }
  }

  /**
   * Asienta el SSR de un pasajero que respondio "No".
   * @returns true solo si BE confirma ATTESTATION_ADOC y ATTESTATION_ATOC
   */
  submit(reservation: Reservation, passenger: Passenger): Observable<boolean> {
    return this._updateDocuments.updateAttestation(reservation, passenger).pipe(
      timeout(CDC_ATTESTATION_TIMEOUT_MS),
      map((res) => isCdcAttestationSaved(res, passenger.nameRefNumber)),
      catchError((error) => {
        this.track(NEW_RELIC_EVENT.CDC_ATTESTATION_SSR_ERROR, reservation, passenger, {
          errorCode: error?.getCode?.() ?? error?.name ?? 'UNKNOWN',
        });
        return of(null);
      }),
      tap((saved) => {
        if (saved === true) {
          this.track(NEW_RELIC_EVENT.CDC_ATTESTATION_SSR_OK, reservation, passenger);
        } else if (saved === false) {
          this.track(NEW_RELIC_EVENT.CDC_ATTESTATION_SSR_ERROR, reservation, passenger, {
            errorCode: 'FIELDS_FAILED',
          });
        }
      }),
      map((saved) => saved === true)
    );
  }

  /**
   * Evidencia de la atestacion (fase 1, mientras BE no tenga BD): PNR, pasajero por numero
   * de nombre, aerolinea, vuelo, origen, destino y fecha. Sin nombre ni documento en claro.
   */
  track(
    event: NEW_RELIC_EVENT,
    reservation: Reservation,
    passenger?: Passenger,
    extra: Record<string, any> = {}
  ): void {
    const summary = reservation?.flightSummary;
    const segment = summary?.currentSegment;
    this._newRelicEventsService.trackEvent(event, {
      reservationCode: reservation?.pnr,
      passengerNameNumber: passenger?.nameRefNumber,
      passengerType: passenger?.paxType,
      hasInfant: !!passenger?.passengerInfant,
      airline: segment?.operatingCarrier,
      flight: summary?.flightNumber,
      departureAirport: summary?.departureAirport,
      arrivalAirport: summary?.arrivalAirport,
      departureDateTime: summary?.departureDateTime,
      legCode: summary?.legCode,
      ...extra,
    });
  }
}
